import React, { useEffect, useMemo, useState } from 'react';
import { Button, FormField, Input, Textarea, Select, LoadingOverlay } from '../components/ui';
import ConistraMark from '../components/ConistraMark';
import {
  getStrategies,
  createStrategy,
  updateStrategy,
  getPillars,
  createPillar,
  updatePillar,
  deletePillar,
  getPlans,
  createPlan,
  updatePlan,
  deletePlan,
} from '../api';
import type {
  Strategy,
  StrategySection,
  ContentPillar,
  ContentPlan,
  CampaignType,
  PageKey,
} from '../types';
import './StrategyPage.css';

type StrategyPageProps = {
  workspaceId: number;
  onNavigate?: (_page: PageKey) => void;
};

/* ── Strategy document helpers ─────────────────────────────────────────────── */

/** Sections consumed by the hero (headline + ledes) instead of the playbook grid. */
const HERO_KEYS = ['north_star', 'target_audience', 'brand_vibe'];

/** Core sections seeded into the editor when writing a strategy from scratch. */
const SECTION_TEMPLATE: { key: string; title: string; hint: string }[] = [
  { key: 'north_star', title: 'North star', hint: 'Mission and long-term goal in one sentence.' },
  { key: 'target_audience', title: 'Target audience', hint: 'Who is this account actually for?' },
  { key: 'brand_vibe', title: 'Brand voice & vibe', hint: 'How the account sounds — personality, energy, tone.' },
  { key: 'image_style', title: 'Image style', hint: 'Visual rules for generated images: colors, composition, mood.' },
  { key: 'text_on_image', title: 'Text on image', hint: 'Rules for overlays: font feel, length, placement.' },
  { key: 'caption_style', title: 'Caption style', hint: 'The rules every caption follows.' },
  { key: 'hashtag_policy', title: 'Hashtag policy', hint: 'None / light (3–5 niche tags) / strong (10–15 mixed).' },
];

function sectionText(sections: StrategySection[], key: string): string {
  return sections.find((s) => s.key === key)?.body?.trim() || '';
}

function prettyTitle(section: StrategySection): string {
  if (section.title.trim()) return section.title.trim();
  return section.key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function slugifyKey(title: string): string {
  return (
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'section'
  );
}

/* ── Campaign helpers ──────────────────────────────────────────────────────── */

type CampaignStatus = 'live' | 'upcoming' | 'evergreen' | 'ended';

const CAMPAIGN_STATUS_ORDER: Record<CampaignStatus, number> = {
  live: 0,
  upcoming: 1,
  evergreen: 2,
  ended: 3,
};

const CAMPAIGN_STATUS_LABEL: Record<CampaignStatus, string> = {
  live: 'Live',
  upcoming: 'Upcoming',
  evergreen: 'Always on',
  ended: 'Ended',
};

const CAMPAIGN_TYPE_LABEL: Record<CampaignType, string> = {
  launch: 'Launch',
  growth: 'Growth push',
  series: 'Series',
  seasonal: 'Seasonal',
  evergreen: 'Evergreen',
  other: 'Campaign',
};

function campaignStatus(plan: ContentPlan): CampaignStatus {
  const today = new Date().toISOString().slice(0, 10);
  if (plan.start_date && plan.start_date > today) return 'upcoming';
  if (plan.end_date && plan.end_date < today) return 'ended';
  if (plan.start_date || plan.end_date) return 'live';
  return 'evergreen';
}

function formatDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

function campaignWindow(plan: ContentPlan): string {
  if (plan.start_date && plan.end_date)
    return `${formatDay(plan.start_date)} – ${formatDay(plan.end_date)}`;
  if (plan.start_date) return `From ${formatDay(plan.start_date)}`;
  if (plan.end_date) return `Until ${formatDay(plan.end_date)}`;
  return 'No fixed window';
}

/* ── Local form state shapes ───────────────────────────────────────────────── */

type EditorSection = { key: string; title: string; body: string; hint?: string };

type CampaignForm = {
  name: string;
  campaign_type: CampaignType;
  objective: string;
  description: string;
  start_date: string;
  end_date: string;
};

const EMPTY_CAMPAIGN: CampaignForm = {
  name: '',
  campaign_type: 'series',
  objective: '',
  description: '',
  start_date: '',
  end_date: '',
};

/* ═══════════════════════════════════════════════════════════════════════════ */

function StrategyPage({ workspaceId, onNavigate }: StrategyPageProps) {
  const [strategy, setStrategy] = useState<Strategy | null>(null);
  const [pillars, setPillars] = useState<ContentPillar[]>([]);
  const [campaigns, setCampaigns] = useState<ContentPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Strategy document editor
  const [editingDoc, setEditingDoc] = useState(false);
  const [editorSections, setEditorSections] = useState<EditorSection[]>([]);
  const [editorCadence, setEditorCadence] = useState('');
  const [editorOrientation, setEditorOrientation] = useState<'portrait' | 'square'>('square');
  const [savingDoc, setSavingDoc] = useState(false);

  // Pillar quick-add
  const [addingPillar, setAddingPillar] = useState(false);
  const [pillarForm, setPillarForm] = useState({ name: '', description: '' });

  // Campaign create / edit
  const [campaignFormOpen, setCampaignFormOpen] = useState(false);
  const [campaignEditId, setCampaignEditId] = useState<number | null>(null);
  const [campaignForm, setCampaignForm] = useState<CampaignForm>(EMPTY_CAMPAIGN);
  const [savingCampaign, setSavingCampaign] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [strategies, pillarData, planData] = await Promise.allSettled([
        getStrategies(),
        getPillars(),
        getPlans(),
      ]);
      if (!mounted) return;
      if (strategies.status === 'fulfilled') {
        setStrategy((strategies.value.results?.[0] as Strategy) || null);
      }
      if (pillarData.status === 'fulfilled') setPillars(pillarData.value.results || []);
      if (planData.status === 'fulfilled') setCampaigns(planData.value.results || []);
      if (strategies.status === 'rejected') setError('Failed to load your strategy.');
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const sections = useMemo(() => strategy?.sections || [], [strategy]);
  const northStar = sectionText(sections, 'north_star');
  const audience = sectionText(sections, 'target_audience');
  const vibe = sectionText(sections, 'brand_vibe');
  const playbook = sections.filter((s) => !HERO_KEYS.includes(s.key) && s.body.trim());

  const sortedCampaigns = useMemo(
    () =>
      [...campaigns].sort(
        (a, b) => CAMPAIGN_STATUS_ORDER[campaignStatus(a)] - CAMPAIGN_STATUS_ORDER[campaignStatus(b)]
      ),
    [campaigns]
  );

  const enabledPillars = pillars.filter((p) => p.is_enabled).length;

  /* ── Strategy document editing ──────────────────────────────────────────── */

  const openDocEditor = () => {
    const existing = strategy?.sections || [];
    const seeded: EditorSection[] =
      existing.length > 0
        ? existing.map((s) => ({
            ...s,
            hint: SECTION_TEMPLATE.find((t) => t.key === s.key)?.hint,
          }))
        : SECTION_TEMPLATE.map((t) => ({ key: t.key, title: t.title, body: '', hint: t.hint }));
    setEditorSections(seeded);
    setEditorCadence(strategy?.posting_cadence || '');
    setEditorOrientation(strategy?.image_orientation || 'square');
    setEditingDoc(true);
    setError('');
  };

  const handleSaveDoc = async () => {
    setSavingDoc(true);
    setError('');
    try {
      // The API rejects sections with an empty body — drop them on save.
      const cleaned = editorSections
        .map((s) => ({
          key: s.key.trim() || slugifyKey(s.title),
          title: s.title.trim(),
          body: s.body.trim(),
        }))
        .filter((s) => s.body.length > 0);
      const payload = {
        sections: cleaned,
        posting_cadence: editorCadence,
        image_orientation: editorOrientation,
      };
      const saved = strategy
        ? await updateStrategy(strategy.id, payload)
        : await createStrategy(workspaceId, payload);
      setStrategy(saved);
      setEditingDoc(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save strategy');
    } finally {
      setSavingDoc(false);
    }
  };

  /* ── Pillars ────────────────────────────────────────────────────────────── */

  const handleAddPillar = async () => {
    if (!pillarForm.name.trim()) return;
    setError('');
    try {
      const created = await createPillar(workspaceId, {
        name: pillarForm.name.trim(),
        description: pillarForm.description.trim(),
      });
      setPillars((prev) => [...prev, created]);
      setPillarForm({ name: '', description: '' });
      setAddingPillar(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add pillar');
    }
  };

  const handleTogglePillar = async (pillar: ContentPillar) => {
    try {
      const updated = await updatePillar(pillar.id, { is_enabled: !pillar.is_enabled });
      setPillars((prev) => prev.map((p) => (p.id === pillar.id ? updated : p)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update pillar');
    }
  };

  const handleDeletePillar = async (id: number) => {
    try {
      await deletePillar(id);
      setPillars((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove pillar');
    }
  };

  /* ── Campaigns ──────────────────────────────────────────────────────────── */

  const openCampaignForm = (plan?: ContentPlan) => {
    if (plan) {
      setCampaignEditId(plan.id);
      setCampaignForm({
        name: plan.name,
        campaign_type: plan.campaign_type || 'series',
        objective: plan.objective || '',
        description: plan.description || '',
        start_date: plan.start_date || '',
        end_date: plan.end_date || '',
      });
    } else {
      setCampaignEditId(null);
      setCampaignForm(EMPTY_CAMPAIGN);
    }
    setCampaignFormOpen(true);
    setError('');
  };

  const handleSaveCampaign = async () => {
    if (!campaignForm.name.trim()) return;
    setSavingCampaign(true);
    setError('');
    try {
      const payload = {
        name: campaignForm.name.trim(),
        campaign_type: campaignForm.campaign_type,
        objective: campaignForm.objective.trim(),
        description: campaignForm.description.trim(),
        start_date: campaignForm.start_date || null,
        end_date: campaignForm.end_date || null,
      };
      if (campaignEditId) {
        const updated = await updatePlan(campaignEditId, payload);
        setCampaigns((prev) => prev.map((c) => (c.id === campaignEditId ? updated : c)));
      } else {
        const created = await createPlan(workspaceId, payload);
        setCampaigns((prev) => [created, ...prev]);
      }
      setCampaignFormOpen(false);
      setCampaignEditId(null);
      setCampaignForm(EMPTY_CAMPAIGN);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save campaign');
    } finally {
      setSavingCampaign(false);
    }
  };

  const handleDeleteCampaign = async (id: number) => {
    try {
      await deletePlan(id);
      setCampaigns((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete campaign');
    }
  };

  /* ── Render ─────────────────────────────────────────────────────────────── */

  if (loading) {
    return (
      <div className="strategy-page">
        <LoadingOverlay label="Opening your brand book…" />
      </div>
    );
  }

  const askOracle = onNavigate ? () => onNavigate('assistant') : undefined;

  return (
    <div className="strategy-page">
      <div className="strat-doc">
        {error && (
          <div className="page-error" role="alert">
            <span aria-hidden="true">⚠</span> {error}
          </div>
        )}

        {/* ═══ Chapter 1 — The strategy document ═══════════════════════════ */}
        {editingDoc ? (
          <DocEditor
            sections={editorSections}
            cadence={editorCadence}
            orientation={editorOrientation}
            saving={savingDoc}
            isNew={!strategy}
            onChangeSections={setEditorSections}
            onChangeCadence={setEditorCadence}
            onChangeOrientation={setEditorOrientation}
            onSave={handleSaveDoc}
            onCancel={strategy ? () => setEditingDoc(false) : undefined}
          />
        ) : !strategy || sections.length === 0 ? (
          <div className="strat-hero-empty">
            <span className="mono-label">Strategy</span>
            <h1 className="strat-title">Every great account runs on a strategy.</h1>
            <p className="strat-lede">
              Who you're for, how you sound, how your content looks — written once, followed on
              every post. The fastest way to get one is to ask the Oracle.
            </p>
            <div className="strat-hero-empty__actions">
              {askOracle && (
                <button className="strat-gradient-btn" onClick={askOracle} type="button">
                  Ask the Oracle
                </button>
              )}
              <Button variant="ghost" onClick={openDocEditor}>
                Write it yourself
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="strat-eyebrow-row">
              <span className="mono-label">Strategy · living document</span>
              <div className="strat-eyebrow-row__actions">
                {strategy.updated_at && (
                  <span className="strat-updated">
                    Updated {formatDay(strategy.updated_at.slice(0, 10))}
                  </span>
                )}
                <button className="strat-edit-btn" onClick={openDocEditor} type="button">
                  Edit
                </button>
              </div>
            </div>

            <h1 className="strat-title">{northStar || 'Your strategy is taking shape.'}</h1>

            {(audience || vibe) && (
              <div className="strat-identity">
                {audience && (
                  <div className="strat-identity__block">
                    <span className="mono-label mono-label--muted">Who it's for</span>
                    <p>{audience}</p>
                  </div>
                )}
                {vibe && (
                  <div className="strat-identity__block">
                    <span className="mono-label mono-label--muted">How it sounds</span>
                    <p>{vibe}</p>
                  </div>
                )}
              </div>
            )}

            <div className="strat-meta-row">
              {strategy.posting_cadence && (
                <span className="strat-chip" title="Posting cadence">
                  ↻ {strategy.posting_cadence}
                </span>
              )}
              <span className="strat-chip" title="Image orientation">
                {strategy.image_orientation === 'portrait' ? '▯ Portrait 9:16' : '□ Square 1:1'}
              </span>
            </div>

            {playbook.length > 0 && (
              <section className="strat-chapter">
                <div className="strat-chapter__head">
                  <span className="mono-label">The playbook</span>
                </div>
                <div className="strat-playbook">
                  {playbook.map((s) => (
                    <div className="strat-card" key={s.key}>
                      <div className="strat-card__label">{prettyTitle(s)}</div>
                      <div className="strat-card__body">{s.body}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {/* ═══ Chapter 2 — Content pillars ═════════════════════════════════ */}
        {!editingDoc && (
          <section className="strat-chapter">
            <div className="strat-chapter__head">
              <span className="mono-label">
                Content pillars
                {pillars.length > 0 && (
                  <span className="strat-count"> · {enabledPillars} active</span>
                )}
              </span>
              <button
                className="strat-add-btn"
                onClick={() => setAddingPillar((v) => !v)}
                type="button"
              >
                {addingPillar ? 'Close' : '+ Add pillar'}
              </button>
            </div>
            <p className="strat-chapter__sub">
              The 4–6 recurring themes the Oracle rotates through so your feed stays varied but
              unmistakably yours.
            </p>

            {addingPillar && (
              <div className="strat-inline-form">
                <FormField label="Pillar name" htmlFor="pillar-name" required>
                  <Input
                    id="pillar-name"
                    value={pillarForm.name}
                    onChange={(e) => setPillarForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Myth-busting, Behind the scenes, Client wins"
                    autoFocus
                  />
                </FormField>
                <FormField label="What lives here" htmlFor="pillar-desc">
                  <Textarea
                    id="pillar-desc"
                    value={pillarForm.description}
                    onChange={(e) => setPillarForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="The angle, tone, and kind of posts this theme produces."
                    rows={2}
                  />
                </FormField>
                <div className="strat-inline-form__actions">
                  <Button variant="primary" size="sm" onClick={handleAddPillar}>
                    Add pillar
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setAddingPillar(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {pillars.length === 0 && !addingPillar ? (
              <div className="strat-empty-note">
                No pillars yet. The Oracle proposes them during activation — or{' '}
                {askOracle ? (
                  <button className="strat-link" onClick={askOracle} type="button">
                    ask it to draft a set
                  </button>
                ) : (
                  'ask the Oracle to draft a set'
                )}{' '}
                from your strategy.
              </div>
            ) : (
              <div className="strat-pillars">
                {pillars.map((pillar, i) => (
                  <div
                    key={pillar.id}
                    className={`strat-pillar${pillar.is_enabled ? '' : ' strat-pillar--off'}`}
                  >
                    <div className="strat-pillar__top">
                      <span className="strat-pillar__num">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span
                        className={`strat-pillar__dot${pillar.is_enabled ? ' strat-pillar__dot--on' : ''}`}
                        title={pillar.is_enabled ? 'Active' : 'Paused'}
                      />
                    </div>
                    <div className="strat-pillar__name">{pillar.name}</div>
                    {pillar.description && (
                      <div className="strat-pillar__desc">{pillar.description}</div>
                    )}
                    <div className="strat-pillar__actions">
                      <button
                        className="strat-link"
                        onClick={() => handleTogglePillar(pillar)}
                        type="button"
                      >
                        {pillar.is_enabled ? 'Pause' : 'Activate'}
                      </button>
                      <button
                        className="strat-link strat-link--danger"
                        onClick={() => handleDeletePillar(pillar.id)}
                        type="button"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ═══ Chapter 3 — Campaigns ═══════════════════════════════════════ */}
        {!editingDoc && (
          <section className="strat-chapter">
            <div className="strat-chapter__head">
              <span className="mono-label">Campaigns</span>
              <button
                className="strat-add-btn"
                onClick={() => (campaignFormOpen ? setCampaignFormOpen(false) : openCampaignForm())}
                type="button"
              >
                {campaignFormOpen && !campaignEditId ? 'Close' : '+ New campaign'}
              </button>
            </div>
            <p className="strat-chapter__sub">
              Time-boxed pushes layered on top of your strategy — a launch, a series, a seasonal
              arc. Posts created under a campaign follow its brief.
            </p>

            {campaignFormOpen && (
              <div className="strat-inline-form">
                <FormField label="Campaign name" htmlFor="camp-name" required>
                  <Input
                    id="camp-name"
                    value={campaignForm.name}
                    onChange={(e) => setCampaignForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Summer challenge, Product launch week"
                    autoFocus
                  />
                </FormField>
                <div className="strat-inline-form__row">
                  <FormField label="Type" htmlFor="camp-type">
                    <Select
                      id="camp-type"
                      value={campaignForm.campaign_type}
                      onChange={(e) =>
                        setCampaignForm((f) => ({
                          ...f,
                          campaign_type: e.target.value as CampaignType,
                        }))
                      }
                    >
                      <option value="launch">Launch</option>
                      <option value="growth">Growth push</option>
                      <option value="series">Series / arc</option>
                      <option value="seasonal">Seasonal</option>
                      <option value="evergreen">Evergreen rotation</option>
                      <option value="other">Other</option>
                    </Select>
                  </FormField>
                  <FormField label="Starts" htmlFor="camp-start">
                    <Input
                      id="camp-start"
                      type="date"
                      value={campaignForm.start_date}
                      onChange={(e) =>
                        setCampaignForm((f) => ({ ...f, start_date: e.target.value }))
                      }
                    />
                  </FormField>
                  <FormField label="Ends" htmlFor="camp-end">
                    <Input
                      id="camp-end"
                      type="date"
                      value={campaignForm.end_date}
                      onChange={(e) => setCampaignForm((f) => ({ ...f, end_date: e.target.value }))}
                    />
                  </FormField>
                </div>
                <FormField
                  label="Objective"
                  htmlFor="camp-objective"
                  hint="What should this campaign achieve? Make it measurable."
                >
                  <Textarea
                    id="camp-objective"
                    value={campaignForm.objective}
                    onChange={(e) => setCampaignForm((f) => ({ ...f, objective: e.target.value }))}
                    placeholder="e.g. Warm the audience up for the course launch — 200 waitlist signups."
                    rows={2}
                  />
                </FormField>
                <FormField label="Description" htmlFor="camp-desc">
                  <Textarea
                    id="camp-desc"
                    value={campaignForm.description}
                    onChange={(e) =>
                      setCampaignForm((f) => ({ ...f, description: e.target.value }))
                    }
                    placeholder="The idea in a few lines. The Oracle expands this into a full brief."
                    rows={2}
                  />
                </FormField>
                <div className="strat-inline-form__actions">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSaveCampaign}
                    loading={savingCampaign}
                  >
                    {campaignEditId ? 'Save campaign' : 'Create campaign'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setCampaignFormOpen(false);
                      setCampaignEditId(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {sortedCampaigns.length === 0 && !campaignFormOpen ? (
              <div className="strat-empty-note">
                Nothing running right now. Start one here — or{' '}
                {askOracle ? (
                  <button className="strat-link" onClick={askOracle} type="button">
                    ask the Oracle to plan a campaign
                  </button>
                ) : (
                  'ask the Oracle to plan a campaign'
                )}{' '}
                and it will write the brief and the posts.
              </div>
            ) : (
              <div className="strat-campaigns">
                {sortedCampaigns.map((plan) => {
                  const status = campaignStatus(plan);
                  const brief = (plan.sections || []).filter((s) => s.body?.trim());
                  return (
                    <div key={plan.id} className={`strat-campaign strat-campaign--${status}`}>
                      <div className="strat-campaign__head">
                        <div className="strat-campaign__badges">
                          <span className={`strat-status strat-status--${status}`}>
                            <span className="strat-status__dot" />
                            {CAMPAIGN_STATUS_LABEL[status]}
                          </span>
                          <span className="strat-type-badge">
                            {CAMPAIGN_TYPE_LABEL[plan.campaign_type] || 'Campaign'}
                          </span>
                        </div>
                        <span className="strat-campaign__window">{campaignWindow(plan)}</span>
                      </div>
                      <div className="strat-campaign__name">{plan.name}</div>
                      {plan.objective && (
                        <div className="strat-campaign__objective">{plan.objective}</div>
                      )}
                      {plan.description && (
                        <p className="strat-campaign__desc">{plan.description}</p>
                      )}
                      {brief.length > 0 && (
                        <details className="strat-campaign__brief">
                          <summary>Oracle brief · {brief.length} sections</summary>
                          {brief.map((s) => (
                            <div className="strat-campaign__brief-section" key={s.key}>
                              <div className="strat-campaign__brief-title">{prettyTitle(s)}</div>
                              <p>{s.body}</p>
                            </div>
                          ))}
                        </details>
                      )}
                      <div className="strat-campaign__actions">
                        <button
                          className="strat-link"
                          onClick={() => openCampaignForm(plan)}
                          type="button"
                        >
                          Edit
                        </button>
                        <button
                          className="strat-link strat-link--danger"
                          onClick={() => handleDeleteCampaign(plan.id)}
                          type="button"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* ═══ Oracle footer ═══════════════════════════════════════════════ */}
        {!editingDoc && (
          <div className="strat-oracle-note">
            <ConistraMark size={30} />
            <div>
              <div className="strat-oracle-note__title">The Oracle</div>
              <p className="strat-oracle-note__text">
                This is my working document — strategy, pillars, and whatever campaign we're
                running. I revisit it as your account grows and adjust from what's actually
                working. Want to change direction or start a push? Just tell me.
              </p>
              {askOracle && (
                <button className="strat-gradient-btn" onClick={askOracle} type="button">
                  Ask the Oracle
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Strategy document editor (edit mode) ─────────────────────────────────── */

function DocEditor({
  sections,
  cadence,
  orientation,
  saving,
  isNew,
  onChangeSections,
  onChangeCadence,
  onChangeOrientation,
  onSave,
  onCancel,
}: {
  sections: EditorSection[];
  cadence: string;
  orientation: 'portrait' | 'square';
  saving: boolean;
  isNew: boolean;
  onChangeSections: (_s: EditorSection[]) => void;
  onChangeCadence: (_v: string) => void;
  onChangeOrientation: (_v: 'portrait' | 'square') => void;
  onSave: () => void;
  onCancel?: () => void;
}) {
  const update = (idx: number, patch: Partial<EditorSection>) => {
    onChangeSections(sections.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  return (
    <div className="strat-editor">
      <div className="strat-eyebrow-row">
        <span className="mono-label">Strategy · editing</span>
      </div>
      <h1 className="strat-title">{isNew ? "Let's write your strategy" : 'Refine your strategy'}</h1>
      <p className="strat-lede">
        Each section is a rule the Oracle follows on every post. Leave a section empty and it will
        be dropped; add sections for anything else that matters to your brand.
      </p>

      <div className="strat-editor__sections">
        {sections.map((s, i) => (
          <div className="strat-editor__section" key={`${s.key}-${i}`}>
            <div className="strat-editor__section-head">
              <input
                className="strat-editor__title-input"
                value={s.title}
                onChange={(e) => update(i, { title: e.target.value })}
                placeholder="Section title"
                aria-label="Section title"
              />
              <button
                className="strat-link strat-link--danger"
                onClick={() => onChangeSections(sections.filter((_, j) => j !== i))}
                type="button"
              >
                Remove
              </button>
            </div>
            <Textarea
              value={s.body}
              onChange={(e) => update(i, { body: e.target.value })}
              placeholder={s.hint || 'Write the rule…'}
              rows={3}
            />
          </div>
        ))}
      </div>

      <button
        className="strat-add-btn strat-add-btn--block"
        onClick={() => onChangeSections([...sections, { key: '', title: '', body: '' }])}
        type="button"
      >
        + Add a section
      </button>

      <div className="strat-editor__meta">
        <FormField
          label="Posting cadence"
          htmlFor="strategy-cadence"
          hint="Your default rhythm — campaigns can override it."
        >
          <Input
            id="strategy-cadence"
            value={cadence}
            onChange={(e) => onChangeCadence(e.target.value)}
            placeholder="e.g. 1× per day at 8 AM"
          />
        </FormField>
        <FormField label="Image orientation" htmlFor="strategy-orientation">
          <Select
            id="strategy-orientation"
            value={orientation}
            onChange={(e) => onChangeOrientation(e.target.value as 'portrait' | 'square')}
          >
            <option value="square">Square — 1:1</option>
            <option value="portrait">Portrait — 9:16</option>
          </Select>
        </FormField>
      </div>
      {orientation === 'portrait' && (
        <p className="strategy-form__note">
          ⚠️ Instagram crops the feed taller than square — add “leave room at the top and bottom”
          to your image style so nothing important gets cut off.
        </p>
      )}

      <div className="strategy-form__actions">
        <Button variant="primary" onClick={onSave} loading={saving}>
          Save strategy
        </Button>
        {onCancel && (
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}

export default StrategyPage;
